import { observer, inject } from "mobx-react";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import { Button, Select, TextArea, TextInput } from "../UI";
import { useInput, inputsHasError } from "../utils/hooks";
import { isNotEmpty, withError } from "../validation";
import { maxLength } from "../validation/length";
import PhotosWidget from "./PhotosWidget";
import { useMobile } from "../utils/mobile";
import { BorderButton } from "../UI/Button/Button";
import MobileHeader from "../layouts/MobileHeader";

const NewDefect = ({
  userStore,
  tenantsStore,
  repairPagesStore
}) => {
  const { t } = useTranslation("applies");
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMobile();
  const [validate, setValidate] = useState(false)
  const defect = location.state?.defect ? location.state.defect : {};
  const department = location.state?.department ? location.state.department : "";
  const APPCODE = "Bdk";
  const mustPic = userStore.applicationAttributes[APPCODE]?.Bdk_MustPic === "Y"
  const [newDefect, setNewDefect] = useState({
    CURDATE: new Date(),
    NAME: userStore.userDetails.NAME,
    KLINE: repairPagesStore.defectList?.length + 1,
    ...defect,
  });

  const [defectDecription, setDefectDecription] = useState(
    !!defect.DEFECTDES
      ? `${!!defect.DEFECTDES ? newDefect.DEFECTDES : ``} ${!!newDefect?.ZCOB_DEFECTSLISTTEXT_SUBFORM?.TEXT
        ? defect?.ZCOB_DEFECTSLISTTEXT_SUBFORM?.TEXT
        : ``
      }`
      : ``
  );

  const [extFiles, setExtFiles] = useState(
    !!newDefect?.ZCOB_DEFECTSLISTPIC_SUBFORM && !!newDefect.EXTFILENAME
      ? [
        { EXTFILENAME: newDefect.EXTFILENAME },
        ...newDefect?.ZCOB_DEFECTSLISTPIC_SUBFORM,
      ]
      : !!newDefect.EXTFILENAME
        ? [{ EXTFILENAME: newDefect.EXTFILENAME }]
        : []
  );

  const [domain, setDomain] = useState(
    newDefect?.CHKPERIODTYPECODE
      ? {
        value: newDefect?.CHKPERIODTYPECODE,
        label: newDefect?.CHKPERIODTYPEDES,
      }
      : null
  );

  const areas =
    repairPagesStore.siteAreas?.map((area) => ({
      value: area.SITEAREADES,
      label: area.SITEAREADES,
    })) || [];

  const [area, setArea] = useState(
    newDefect?.SITEAREADES
      ? {
        value: newDefect?.SITEAREADES,
        label: newDefect?.SITEAREADES,
      }
      : null
  );

  const defectLocations =
    repairPagesStore.locations?.map((location) => ({
      value: location.LOCATIONDES,
      label: location.LOCATIONDES,
    })) || [];

  const [defectLocation, setDefectLocation] = useState(
    newDefect?.LOCATIONDES
      ? {
        value: newDefect?.LOCATIONDES,
        label: newDefect?.LOCATIONDES,
      }
      : null
  );

  const domains =
    repairPagesStore.domains?.map((domain) => ({
      value: domain.CHKPERIODTYPECODE,
      label: domain.CHKPERIODTYPEDES,
    })) || [];

  const addNewDefect = () => {
    const valid = validateDefectRow();
    if (valid) {
      const defects = [...repairPagesStore.defectList];

      defects.push(newDefect);

      repairPagesStore.setDefectList(defects);
      if (location.pathname.includes("/tenant")) {
        navigate("/tenant/new-repair-page", { state: { department } });
      } else {
        navigate("/bedek/new-repair-page", {
          state: { bedek: true, department },
        });
      }
    }
  };

  const editNewDefect = () => {
    const valid = validateDefectRow();
    if (valid) {
      const defects = [...repairPagesStore.defectList];

      const defectIndex = defects.findIndex(
        (defect) => defect.KLINE === newDefect.KLINE
      );

      if (defectIndex !== -1) {
        defects[defectIndex] = newDefect;
        repairPagesStore.setDefectList(defects);
        if (location.pathname.includes("/tenant")) {
          navigate("/tenant/new-repair-page");
        } else {
          navigate("/bedek/new-repair-page", {
            state: { bedek: true },
          });
        }
      }
    }
  };

  useEffect(() => {
    const tempExts = [...extFiles];
    setNewDefect({
      ...newDefect,
      EXTFILENAME: tempExts?.shift()?.EXTFILENAME || null,
      ZCOB_DEFECTSLISTPIC_SUBFORM: tempExts,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extFiles]);

  const handleRemoveImg = (file) => {
    const filteredExtFiles = extFiles.filter(
      (item) => item.EXTFILENAME !== file.EXTFILENAME
    );
    setExtFiles(filteredExtFiles);
  };

  const isNotEmptyWithError = withError(
    isNotEmpty,
    t("text_field_empty_error")
  );

  const maxLengthWithError = withError(
    (input) => maxLength(input, 120),
    t("text_field_note_length_error", { length: 120 })
  );

  const description = useInput({
    validation: [isNotEmptyWithError, maxLengthWithError],
    placeholder: t("description_placeholder"),
    value: defectDecription,
  });

  useEffect(() => {
    const tempExts = [...extFiles];
    setNewDefect({
      ...newDefect,
      EXTFILENAME: tempExts?.shift()?.EXTFILENAME || null,
      ZCOB_DEFECTSLISTPIC_SUBFORM: tempExts,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extFiles]);

  const klineInput = useInput({
    validation: [isNotEmptyWithError],
    value: newDefect.KLINE,
  });

  const areaInput = useInput({
    label: t("area"),
    validation: [isNotEmptyWithError],
    placeholder: t("area_placeholder"),
    value: area || "",
  });

  const locationInput = useInput({
    label: t("location"),
    validation: [isNotEmptyWithError],
    placeholder: t("location_placeholder"),
    value: defectLocation || "",
  });

  const domainInput = useInput({
    label: t("domain"),
    validation: [isNotEmptyWithError],
    placeholder: t("domain_placeholder"),
    value: domain || "",
  });

  const handleAreaChange = (value) => {
    setArea(value);
    setNewDefect({ ...newDefect, SITEAREADES: value?.value });
  };

  const handleLocationChange = (value) => {
    setDefectLocation(value);
    setNewDefect({ ...newDefect, LOCATIONDES: value?.value });
  };

  const handleDomainChange = (value) => {
    setDomain(value);
    domainInput.error = false;
    domainInput.helperText = "";
    setNewDefect({
      ...newDefect,
      CHKPERIODTYPECODE: value?.value,
      CHKPERIODTYPEDES: value?.label,
    });
  };

  const cancel = () => {
    tenantsStore.selectedSiteSpace?.ISONLYBEDEK === "Y"
      ? navigate("/bedek/new-repair-page")
      : navigate("/tenant/new-repair-page");
  };


  const validateDefectRow = () => {
    let validFiles = true
    setValidate(true)

    if (mustPic && extFiles.length === 0) {
      validFiles = false
    }

    const valid = !inputsHasError(
      domainInput,
      locationInput,
      areaInput,
      klineInput,
      description
    );

    return valid && validFiles
  };

  const handleDescriptionChange = (value) => {
    setNewDefect({
      ...newDefect,
      DEFECTDES: value.substring(0, 68),
      ZCOB_DEFECTSLISTTEXT_SUBFORM: { TEXT: value.substring(68) },
    });

    setDefectDecription(value);
  };

  return (
    <>
      <MobileHeader step={true} />
      <div
        style={{
          width: "100%",
          height: "91vh",
          overflowY: "auto",
          backgroundColor: "rgb(255, 255, 255)",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-start",
            alignItems: "start",
            width: "100%",
            gap: 8,
            padding: "10px 20px",
          }}
        >
          <TextInput
            divStyle={{
              display: "flex",
              maxWidth: 150,
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 12,
              flexBasis: "15%",
              height: 42,
              padding: "0 !important",
            }}
            {...klineInput}
            variant="outlined"
            label={t("kline")}
            error={!klineInput.error}
            helperText={klineInput.helperText}
          ></TextInput>
          <Select
            {...areaInput}
            style={{
              textAlign: "right",
              minWidth: 150,
              flexBasis: "85%",
              height: 42,
            }}
            options={areas || []}
            isMobile={isMobile}
            onChange={(value) => handleAreaChange(value)}
            error={!areaInput.error}
            helperText={areaInput.helperText}
          ></Select>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 26,
            padding: "0 20px",
          }}
        >
          <Select
            {...locationInput}
            style={{
              textAlign: "right",
              minWidth: 150,
              flexBasis: "85%",
              height: 42,
            }}
            options={defectLocations || []}
            onChange={(value) => handleLocationChange(value)}
            error={!locationInput.error}
            helperText={locationInput.helperText}
            isMobile={isMobile}
          ></Select>
          <Select
            {...domainInput}
            style={{
              textAlign: "right",
              minWidth: 150,
              flexBasis: "85%",
              height: 42,
            }}
            options={domains || []}
            onChange={(value) => handleDomainChange(value)}
            error={!domainInput.error}
            helperText={domainInput.helperText}
            isMobile={isMobile}
          ></Select>
          <div style={{ flexBasis: "50%" }}>
            <div style={{ flexBasis: "50%" }}>
              <TextArea
                name="description"
                rows={4}
                cols={90}
                onBlur={(e) => handleDescriptionChange(e.target.value)}
                onChange={(e) => handleDescriptionChange(e.target.value)}
                {...description}
                variant="outlined"
                label={t("defect_description")}
                multiple={true}
                valid={!description.error}
                helperText={description.helperText}
              ></TextArea>
            </div>
            <PhotosWidget
              limit={5}
              isEdit={true}
              handleRemove={handleRemoveImg}
              photosList={extFiles}
              setPhotos={setExtFiles}
              isMobile={isMobile}
              valid={!!mustPic && !!validate ? extFiles.length > 0 : true}
            />
          </div>
          <div
            style={{
              display: "flex",
              gap: 20,
              flex: "1 1 0%",
              flexDirection: "row",
              flexWrap: "nowrap",
              justifyContent: "center",
              alignItems: "center",
              margin: "auto",
              flexBasis: "100%",
              height: 70,
              backgroundColor: theme.color.white,
              borderTop: "1px solid rgba(234, 238, 244, 1)",
            }}
          >
            <Button
              style={{ height: 46, width: 140, marginTop: 0 }}
              onClick={() =>
                location.state?.edit ? editNewDefect() : addNewDefect()
              }
            >
              {t("approve")}
            </Button>
            <BorderButton
              height={46}
              width={90}
              padding={0}
              text={t("cancel")}
              onClick={cancel}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default inject(
  "userStore",
  "tenantsStore",
  "repairPagesStore",
)(observer(NewDefect));
